import BaseStockProvider from '../BaseStockProvider';

/**
 * Giphy sticker provider
 * Free API: https://developers.giphy.com/docs/api
 * 
 * Giphy API Guidelines:
 * 1. Attribution required - Display "Powered by GIPHY" or GIPHY logo
 * 2. Must link back to GIPHY
 * 3. Rate limit: 42 requests per hour (free tier), 1000/hour (SDK key)
 * 4. Free tier available with registration
 */
class GiphyProvider extends BaseStockProvider {
  constructor(apiKey = null) {
    super({
      name: 'Giphy',
      apiKey: apiKey || process.env.REACT_APP_GIPHY_API_KEY,
      baseUrl: 'https://api.giphy.com/v1',
      mediaTypes: ['sticker'], // Only stickers - Tenor handles GIFs
      requiresAuth: true,
    });
  }

  /**
   * Search for stickers on Giphy (GIFs handled by Tenor)
   */
  async search({ query, mediaType = 'sticker', page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Giphy API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    // Only handle stickers, not GIFs
    if (mediaType === 'gif') {
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      // Giphy uses offset-based pagination
      const offset = (page - 1) * perPage;
      
      // Always use stickers endpoint
      const endpoint = 'stickers';
      
      const response = await fetch(
        `${this.baseUrl}/${endpoint}/search?api_key=${this.apiKey}&q=${encodeURIComponent(query)}&limit=${perPage}&offset=${offset}&rating=g&lang=en`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Giphy API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Normalize and filter out invalid formats
      const normalizedItems = (data.data || []).map(item => this.normalizeItem(item, mediaType));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: offset + perPage < (data.pagination?.total_count || 0),
        total: data.pagination?.total_count || 0,
        page: page,
      };
    } catch (error) {
      console.error('Giphy search error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Get trending stickers (GIFs handled by Tenor)
   */
  async getPopular({ mediaType = 'sticker', page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Giphy API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    // Only handle stickers, not GIFs
    if (mediaType === 'gif') {
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      const offset = (page - 1) * perPage;
      
      // Always use stickers endpoint
      const endpoint = 'stickers';
      
      const response = await fetch(
        `${this.baseUrl}/${endpoint}/trending?api_key=${this.apiKey}&limit=${perPage}&offset=${offset}&rating=g`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Giphy API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Normalize and filter out invalid formats
      const normalizedItems = (data.data || []).map(item => this.normalizeItem(item, mediaType));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: offset + perPage < (data.pagination?.total_count || 0),
        total: data.pagination?.total_count || 0,
        page: page,
      };
    } catch (error) {
      console.error('Giphy getPopular error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Normalize Giphy item to common format
   */
  normalizeItem(item, mediaType) {
    // Get the best quality formats
    const original = item.images?.original;
    const preview = item.images?.preview_gif || item.images?.fixed_width_small;
    
    return {
      id: `giphy_${item.id}`,
      provider: 'giphy',
      name: item.title || 'Sticker',
      thumbnail: preview?.url || original?.url,
      url: original?.url,
      downloadUrl: original?.url,
      type: mediaType,
      category: 'Stock',
      description: item.title,
      size: original?.size ? `${(original.size / (1024 * 1024)).toFixed(1)} MB` : null,
      width: parseInt(original?.width) || null,
      height: parseInt(original?.height) || null,
      author: {
        name: item.username || 'Giphy',
        username: item.username || 'giphy',
        profileUrl: item.user?.profile_url || 'https://giphy.com',
      },
      sourceUrl: item.url,
      attribution: 'Powered by GIPHY',
      attributionUrl: 'https://giphy.com',
      isUserUpload: false,
      // Giphy-specific fields
      giphyId: item.id,
      slug: item.slug,
      rating: item.rating,
      trendingDateTime: item.trending_datetime,
    };
  }

  /**
   * Get attribution text for display
   */
  getAttributionText(item) {
    return 'Powered by GIPHY';
  }
}

export default GiphyProvider;